<script setup lang="ts">
/**
 * Frame for the legal pages rendered from content/legal (Terms versions,
 * Privacy Policy): same head and rule as the imprint, then the German text.
 * `html` comes from app/utils/legal-documents.ts only (the repo's own
 * Markdown), never from user input. The default slot follows the text
 * (e.g. the list of earlier Terms versions).
 */
defineProps<{
  eyebrow: string
  title: string
  lead: string
  html: string
  /** Line above the text, e.g. "Fassung 2026-11-01, Stand 01.11.2026" */
  status?: string
  /** Makes the status line link to a permanent URL (the versioned Terms) */
  statusTo?: string
  /** Marks the status line as a draft that is not valid */
  draft?: boolean
}>()
</script>

<template>
  <main id="main" class="legal-doc">
    <div class="section-shell legal-doc-head shell-gutter">
      <span class="eyebrow">{{ eyebrow }}</span>
      <h1 class="legal-doc-title">{{ title }}</h1>
      <p class="copy-muted legal-doc-lead">{{ lead }}</p>
    </div>

    <div class="legal-doc-body">
      <span class="section-rule" aria-hidden="true" />
      <div class="section-shell legal-doc-shell">
        <p v-if="status" class="legal-doc-status" :class="{ 'is-draft': draft }" lang="de">
          <NuxtLink v-if="statusTo" :to="statusTo" class="nav-link legal-doc-status-link">{{ status }}</NuxtLink>
          <template v-else>{{ status }}</template>
        </p>
        <article class="legal-prose" lang="de" v-html="html" />
        <slot />
      </div>
    </div>
  </main>
</template>

<style scoped>
.legal-doc {
  padding-top: clamp(150px, 17vh, 190px);
}

.legal-doc-head {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 16px;
  padding-bottom: clamp(56px, 7vw, 88px);
}

.legal-doc-title {
  font-size: clamp(2.4rem, 4.4vw, 3.6rem);
  font-weight: 700;
  line-height: 1.04;
  letter-spacing: -0.03em;
  color: var(--nb-ink);
}

.legal-doc-lead {
  max-width: 52ch;
  font-size: 1.06rem;
}

.legal-doc-body {
  position: relative;
  padding: clamp(48px, 6vw, 80px) 0 24px;
}

.legal-doc-shell {
  display: flex;
  flex-direction: column;
  gap: 28px;
}

/* Version line: mono like the imprint's "Last updated", but never case-changed:
   the label is case-sensitive (LEGAL_TERMS_VERSION, /terms/<label>), so the
   line shows it exactly as stored. A draft is a marked pill. */
.legal-doc-status {
  align-self: flex-start;
  margin-inline: var(--nb-gutter);
  font-family: var(--font-mono);
  font-size: 0.8rem;
  letter-spacing: 0.01em;
  color: var(--nb-ink-faint);
}

.legal-doc-status.is-draft {
  padding: 6px 12px;
  border: 1px solid var(--nb-accent-border);
  border-radius: var(--radius-pill);
  background: var(--nb-accent-tint);
  color: var(--nb-ink);
}

.legal-doc-status-link {
  color: inherit;
}

.legal-prose {
  display: flex;
  flex-direction: column;
  gap: 14px;
  max-width: 76ch;
  min-width: 0;
  padding-inline: var(--nb-gutter);
}

.legal-prose :deep(h2) {
  margin-top: 22px;
  padding-top: 26px;
  border-top: 1px solid var(--nb-hairline);
  font-size: 1.12rem;
  font-weight: 600;
  letter-spacing: -0.01em;
  color: var(--nb-ink);
  scroll-margin-top: 110px;
}

.legal-prose :deep(h3) {
  margin-top: 10px;
  font-size: 1rem;
  font-weight: 600;
  color: var(--nb-ink);
  scroll-margin-top: 110px;
}

.legal-prose :deep(p),
.legal-prose :deep(li) {
  font-size: 0.98rem;
  line-height: 1.7;
  color: var(--nb-ink-muted);
  overflow-wrap: anywhere;
}

.legal-prose :deep(ul),
.legal-prose :deep(ol) {
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding-left: 1.25rem;
}

.legal-prose :deep(ul) {
  list-style: disc;
}

.legal-prose :deep(ol) {
  list-style: decimal;
}

.legal-prose :deep(a) {
  color: var(--nb-ink);
  text-decoration: underline;
  text-underline-offset: 3px;
}

.legal-prose :deep(strong) {
  color: var(--nb-ink);
}

/* Wide tables scroll inside themselves, never the page */
.legal-prose :deep(table) {
  display: block;
  max-width: 100%;
  overflow-x: auto;
  border-collapse: collapse;
  font-size: 0.92rem;
}

.legal-prose :deep(th),
.legal-prose :deep(td) {
  padding: 8px 10px;
  border-bottom: 1px solid var(--nb-hairline);
  text-align: left;
  vertical-align: top;
  color: var(--nb-ink-muted);
}

.legal-prose :deep(th) {
  color: var(--nb-ink);
  font-weight: 600;
}

.legal-prose :deep(blockquote) {
  padding-left: 14px;
  border-left: 2px solid var(--nb-hairline-strong);
}

.legal-prose :deep(hr) {
  border: 0;
  border-top: 1px solid var(--nb-hairline);
}

.legal-prose :deep(code) {
  font-family: var(--font-mono);
  font-size: 0.86em;
  color: var(--nb-ink);
}
</style>
